import { inject, Injectable } from "@angular/core";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { MonitoringProductData } from "@domain/project/monitoring-product-data/monitoring-product-data";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { catchError, forkJoin, map, Observable, of } from "rxjs";

type MonitoringConfig = ProductConfiguration & { getAllItems(): MonitoringProductItem[] };

@Injectable({ providedIn: "root" })
export class MonitoringConfigDataService {
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);

  assignProductToConfiguration<T extends MonitoringConfig>(
    configs: T[],
    uniqueConfigs: T[],
    category: MonitoringCategory,
  ): Observable<MonitoringConfigProductMap[]> {
    if (uniqueConfigs.length === 0) return of([]);
    uniqueConfigs.sort((a, b) => parseInt(a.positionNumber!) - parseInt(b.positionNumber!));
    return forkJoin(
      uniqueConfigs.map((uniqueConfig) =>
        this.getProducts(uniqueConfig, category).pipe(
          map((data) => {
            const placedProductsCount = configs.filter((c) => c.id === uniqueConfig.id).length;
            return new MonitoringConfigProductMap(uniqueConfig, data ?? [], placedProductsCount);
          }),
        ),
      ),
    );
  }

  private getProducts<T extends MonitoringConfig>(config: T, category: MonitoringCategory): Observable<MonitoringProductData[]> {
    const quantities = MonitoringProductItem.buildDeviceQuantityMap(config.getAllItems());
    const matNr = Array.from(quantities.keys());
    if (matNr.length === 0) return of([]);
    return this.monitoringProductService.loadDevicesFromDB(matNr, category, this.productService.getFilterCountry()).pipe(
      map((devices) =>
        devices.map((device) => {
          const data = MonitoringProductData.create(device, quantities.get(device.matNr) ?? 1);
          data.positionId = config.positionNumber;
          return data;
        }),
      ),
      catchError(() => of([])),
    );
  }
}
